import type { MediaId } from '@app/media';
import type { SandboxLocaleTag } from '@app/shared/i18n/locale-meta';
import type { ColorScheme, PreloadValue, TextDirection } from '@app/shared/sandbox-listener';
import type { SourceId } from '@app/shared/sources';
import type { Skin, Styling } from '@app/types';
import { forwardRef, useEffect, useRef, useState } from 'react';

type PreviewProps = {
  pagePath: string;
  media: MediaId;
  skin: Skin;
  styling: Styling;
  source: SourceId;
  autoplay: boolean;
  muted: boolean;
  loop: boolean;
  preload: PreloadValue;
  locale: SandboxLocaleTag;
  accentColor: string;
  width: number;
  scheme: ColorScheme;
  direction: TextDirection;
  onLoad: () => void;
};

export const Preview = forwardRef<HTMLIFrameElement, PreviewProps>(function Preview(
  {
    pagePath,
    media,
    skin,
    styling,
    source,
    autoplay,
    muted,
    loop,
    preload,
    locale,
    accentColor,
    width,
    scheme,
    direction,
    onLoad,
  },
  ref
) {
  const buildUrl = (base: string, bustCache = false) => {
    const params = new URLSearchParams({
      media,
      skin,
      styling,
      source,
      autoplay: autoplay ? '1' : '0',
      muted: muted ? '1' : '0',
      loop: loop ? '1' : '0',
      preload,
      locale,
      width: String(width),
      scheme,
      dir: direction,
    });

    if (accentColor) params.set('accent', accentColor);

    if (bustCache) params.set('_', String(Date.now()));

    return `${base}?${params}`;
  };

  const reloadOnLocale = pagePath === '/cdn/';

  // Capture the initial query so the iframe doesn't reload when autoplay/muted
  // toggle — those changes are streamed in via postMessage.
  const [iframeUrl, setIframeUrl] = useState(() => buildUrl(pagePath));
  const openUrl = buildUrl(pagePath);
  const previousLocaleRef = useRef(locale);

  // keep iframe `src` locale in sync; other toggles use postMessage.
  // oxlint-disable-next-line react/exhaustive-deps
  useEffect(() => {
    if (previousLocaleRef.current === locale) return;

    previousLocaleRef.current = locale;
    setIframeUrl(buildUrl(pagePath, reloadOnLocale));
  }, [locale, pagePath, reloadOnLocale]);

  return (
    <main className="relative min-h-0 flex-1 bg-zinc-50 dark:bg-zinc-900">
      <a
        href={openUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-3 right-3 z-10 inline-flex h-7 items-center gap-1 rounded-md bg-white bg-clip-border px-2.5 text-xs font-medium text-zinc-600 shadow-xs ring shadow-black/20 ring-zinc-800/10 transition-colors hover:bg-zinc-50 hover:text-zinc-950 dark:bg-zinc-800 dark:text-zinc-300 dark:ring-white/10 dark:hover:bg-zinc-800 dark:hover:text-zinc-50"
        title="Open in new tab"
      >
        Open
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="size-3"
          aria-hidden="true"
        >
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" x2="21" y1="14" y2="3" />
        </svg>
      </a>
      <iframe
        ref={ref}
        src={iframeUrl}
        onLoad={onLoad}
        className="absolute inset-0 h-full w-full border-0"
        title="player demo"
      />
    </main>
  );
});
